import type { AppContext, PaginationParams } from '../../types/context.types';
import { QueryConfig, QueryConfigOptions } from '../../types/query.types';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useCallback, useState } from 'react';
import { getCacheKey } from '../../utils/getCacheKey';
import { useRouter } from '../useRouter';
import { useSession } from '../../Session/useSession';
import { useApiClient } from '../useApiClient/useApiClient';

export type UseApiQueryOptions<R, P> = {
  params?: P; // extra fields to pass to the queryFn
  toastsEnabled?: boolean;
  pagination?: PaginationParams;
} & Partial<UseQueryOptions<R>> &
  Partial<QueryConfigOptions>;

// Custom hook to wrap react query and inject api and session
export function useApiQuery<R, P = any>(
  query: QueryConfig<R, P>,
  hookOptions?: UseApiQueryOptions<R, P>
) {
  const options: UseApiQueryOptions<R, P> = {
    toastsEnabled: true,
    ...omit(query, 'queryFn'),
    ...hookOptions,
  };
  const [pagination, setPagination] = useState<PaginationParams | undefined>(
    options.pagination
  );
  const queryClient = useQueryClient();
  const session = useSession();
  const router = useRouter();
  const api = useApiClient({
    prefix: options.apiPrefix || 'backend',
    baseURLOverride: options.pagination?.currentUrl,
  });
  const appContext = {
    api,
    session: session,
    params: options.params as P,
    router,
    queryClient,
    pagination,
  } as AppContext;

  const isWaitingForSession =
    options.waitForSession && !session.isAuthenticated;

  const result = useQuery({
    ...options,
    queryKey: getCacheKey(
      query.queryFn,
      appContext,
      options.scope || query.scope
    ),
    queryFn: (): Promise<R> | R => query.queryFn(appContext),
    enabled: (options?.enabled ?? true) && !isWaitingForSession,
  } as UseQueryOptions<R>);

  return {
    ...result,
    isLoading:
      result.isLoading || (options.waitForSession && session.isLoading),
    session,
    params: options.params,
    router,
    queryClient,
    setPaginationUrl: useCallback(
      (currentUrl: string) =>
        setPagination({ ...(pagination || {}), currentUrl }),
      []
    ),
  };
}
